import {
  Component,
  input,
  effect,
  untracked,
  inject,
  signal,
  computed,
  output,
  HostListener,
  ViewChild,
  ElementRef,
  OnInit,
  OnDestroy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  DomSanitizer,
  SafeHtml,
  SafeResourceUrl,
} from '@angular/platform-browser';
import {
  buildSandboxCspMetaTag,
  isTrustedAppResourceUrl,
} from '../security/sandbox-security.util';

/**
 * Returns a daily cache-busting version string in YYYYMMDD format (e.g. "20261029")
 * so large shared bundles like rapider-components.js are cached by the browser per day
 * instead of re-downloading on every microapp page navigation.
 */
function getDailyCacheVersion(): string {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}${month}${day}`;
}

export interface AppThemeOption {
  key: string;
  name: string;
  file: string;
  primaryColor: string;
  density: 'comfortable' | 'compact';
  description?: string;
  aliases?: string[];
}

export const RAPIDER_APP_THEMES: readonly AppThemeOption[] = [
  {
    key: 'default',
    name: 'Default (Editorial)',
    file: 'theme-default.css',
    primaryColor: '#6854df',
    density: 'comfortable',
    description:
      'Spacious editorial layout with Rapider brand primary (#6854df), #252840 hero cards, and 14px geometry.',
  },
  {
    key: 'obsidian-compact',
    aliases: ['compact-black', 'compact-obsidian'],
    name: 'Obsidian Compact',
    file: 'theme-obsidian-compact.css',
    primaryColor: '#252840',
    density: 'compact',
    description:
      'High-density executive theme with #252840 Obsidian Slate primary, 8px architectural corners, and compact tables & KPIs.',
  },
];

export function normalizeAppThemeKey(raw?: string | null): string {
  const val = String(raw || '').trim().toLowerCase();
  if (!val || val === 'default') return 'default';
  if (
    val === 'obsidian-compact' ||
    val === 'compact-black' ||
    val === 'compact-obsidian'
  ) {
    return 'obsidian-compact';
  }
  if (/^[a-z0-9][a-z0-9-]{0,48}$/.test(val)) {
    return val;
  }
  return 'default';
}

/**
 * PURE FUNCTION: Wraps raw HTML/JS code into a complete HTML document structure.
 * Injects the Tailwind CSS CDN, NG-Zorro styles, and custom theme definitions to ensure AI-generated components render correctly.
 * @param {string} rawCode - The raw HTML string snippet.
 * @param {string} themeCss - The raw CSS string from the theme file to inject into Tailwind compiler.
 * @param {string} tailwindCdnUrl - The URL to the Tailwind CDN script.
 * @param {Record<string, string>} routeParams - The route parameters.
 * @returns {string} The fully formed HTML document string.
 */
export function buildSandboxHtml(
  rawCode: string,
  themeCss = '',
  tailwindCdnUrl = 'https://cdn.jsdelivr.net/npm/@tailwindcss/browser@4',
  routeParams: Record<string, string> = {},
  assetBaseUrl = '',
  initialSwrCache: Record<string, string> = {},
  activeProjectId = '',
  appThemeKey = 'default',
  appThemeCss = '',
): string {
  const safeThemeKey = normalizeAppThemeKey(appThemeKey);
  const isBrowser = typeof window !== 'undefined';

  // CRITICAL FIX: Establish the absolute host origin so we can build a `<base>` tag
  const hostOrigin = isBrowser
    ? window.location.origin
    : 'http://localhost:4200';

  // CRITICAL FIX: Read the dark mode state directly from the parent document's <html> tag,
  // NOT the OS-level matchMedia, so it perfectly syncs with the user's manual theme toggle.
  const isDark =
    isBrowser && document.documentElement.classList.contains('dark');
  const activeClassMode = isDark ? 'class="dark"' : '';

  // 1. URL to your compiled Angular Elements / Web Components bundle
  // Uses YYYYMMDD daily cache key (e.g. ?v=20261029) so the browser caches it per day across page navigations.
  const customComponentsBundle = `${hostOrigin}/assets/js/rapider-components.js?v=${getDailyCacheVersion()}`;

  // 2. Match the FontAwesome and Google Fonts exactly as they appear in your main index.html
  const fontAwesomeCdnUrl = 'https://kit.fontawesome.com/5b23df26dc.js';

  // CRITICAL FIX: cdnjs does not host ng-zorro-antd. Switched to jsdelivr to successfully fetch the CSS.
  const ngZorroCdnUrl =
    'https://cdn.jsdelivr.net/npm/ng-zorro-antd@21.2.1/ng-zorro-antd.min.css';

  let completeHtml = rawCode || '';

  // 3. Strip out AI hallucinated script tags for tailwind/fontawesome to prevent double-loading
  completeHtml = completeHtml.replace(
    /<script[^>]*tailwindcss[^>]*><\/script>/gi,
    '',
  );
  completeHtml = completeHtml.replace(
    /<script[^>]*fontawesome[^>]*><\/script>/gi,
    '',
  );
  completeHtml = completeHtml.replace(
    /<script[^>]*rapider-components[^>]*><\/script>/gi,
    '',
  );
  completeHtml = completeHtml.replace(
    /<link[^>]*rapider-tailwind-theme[^>]*>/gi,
    '',
  ); // Strip AI hallucinated theme link
  completeHtml = completeHtml.replace(/<link[^>]*ng-zorro-antd[^>]*>/gi, ''); // Strip AI hallucinated zorro link

  // Replace /assets/... with the provided assetBaseUrl ONLY if it belongs to a trusted origin (e.g. host or DigitalOcean Spaces)
  const safeAssetBaseUrl =
    assetBaseUrl && isTrustedAppResourceUrl(assetBaseUrl) ? assetBaseUrl : '';
  let normalizedBase = '';
  if (safeAssetBaseUrl) {
    // Strip trailing slash from safeAssetBaseUrl
    normalizedBase = safeAssetBaseUrl.replace(/\/$/, '');
    // If it explicitly ends with /assets, strip it so we can append it uniformly
    if (normalizedBase.endsWith('/assets')) {
      normalizedBase = normalizedBase.slice(0, -7);
    }
    // Replace absolute (/assets/...) and relative (./assets/...) paths inside href/src attributes
    completeHtml = completeHtml.replace(
      /(href|src)=["'](?:\.\/|\/)?assets\/(.*?)["']/gi,
      `$1="${normalizedBase}/assets/$2"`,
    );
  }

  // 4. CRITICAL FIX: Inject CSP meta tag and <base> tag. This restricts network/script origins to trusted
  // Rapider & DigitalOcean CDN hosts and ensures relative assets resolve cleanly.
  // We use data-rapider-injected="true" so the Drag Drop Builder knows to strip these out before saving the code.
  let injectedHeadAssets = `${buildSandboxCspMetaTag(hostOrigin)}\n<base data-rapider-injected="true" href="${safeAssetBaseUrl ? normalizedBase + '/assets/' : hostOrigin + '/'}">\n`;

  // Inject the theme CSS directly as a text/tailwindcss block so the CDN compiler parses the @theme directive natively
  if (themeCss) {
    injectedHeadAssets += `<style data-rapider-injected="true" type="text/tailwindcss">\n${themeCss}\n</style>\n`;
  }

  // Always inject our controlled versions of the assets
  injectedHeadAssets += `<script data-rapider-injected="true" src="${tailwindCdnUrl}"></script>\n`;
  injectedHeadAssets += `<link data-rapider-injected="true" rel="stylesheet" href="${ngZorroCdnUrl}">\n`;

  // Load rapider-components.js (IIFE bundle) as a classic deferred script so opaque null-origin sandboxes do not require CORS module headers
  injectedHeadAssets += `<script data-rapider-injected="true" src="${customComponentsBundle}" defer onerror="document.body.innerHTML = '<div style=\\'background:#fef2f2; color:#991b1b; padding:16px; margin-bottom:16px; border:1px solid #f87171; border-radius:8px; font-family:sans-serif;\\'><b>Error:</b> Failed to load Custom Components Bundle. Check your network tab.</div>' + document.body.innerHTML; console.error('Failed to load Web Components Bundle');"></script>\n`;
  injectedHeadAssets += `<script data-rapider-injected="true" src="${fontAwesomeCdnUrl}" crossorigin="anonymous"></script>\n`;
  injectedHeadAssets += `<link data-rapider-injected="true" href="https://fonts.googleapis.com/icon?family=Material+Icons" rel="stylesheet" crossorigin="anonymous">\n`;
  injectedHeadAssets += `<link data-rapider-injected="true" rel="stylesheet" href="https://cdn.jsdelivr.net/npm/katex@0.16.11/dist/katex.min.css" crossorigin="anonymous">\n`;

  // Inject structural CSS needed by the Drag Drop Builder to highlight components inside the iframe
  injectedHeadAssets += `
    <style data-rapider-injected="true">
      .show-comment-outlines [data-rapider-comment-id] {
        outline: 2px dashed orangered !important;
        outline-offset: -2px;
      }
      .comment-hover-target {
        outline: 2px dashed var(--color-danger) !important;
        outline-offset: -2px;
        cursor: crosshair !important;
      }
      /* Universal Dark Mode CSS Token & Container Fallbacks */
      html.dark {
        color-scheme: dark;
        --body-background: #0f172a;
        --component-inner: #1e293b;
        --component-background: #1e293b;
        --heading-color: #f8fafc;
        --primary-text-color: #f1f5f9;
        --secondary-text-color: #94a3b8;
        --border-color: #334155;
        --secondary-border: #334155;
        --hover-default: #334155;
        --crm-bg: #0f172a;
        --crm-surface: #1e293b;
        --crm-surface-muted: #0f172a;
        --crm-text: #f1f5f9;
        --crm-muted: #94a3b8;
        --crm-border: #334155;
        --crm-border-strong: #475569;
      }
      html.dark, html.dark body {
        background-color: #0f172a !important;
        color: #f1f5f9 !important;
      }
      html.dark .bg-white:not([class*="dark:bg-"]),
      html.dark .bg-slate-50:not([class*="dark:bg-"]),
      html.dark .bg-gray-50:not([class*="dark:bg-"]),
      html.dark .panel:not(.dark),
      html.dark .card:not(.dark),
      html.dark .metric:not(.dark),
      html.dark .crm-data-panel,
      html.dark .pagecard {
        background-color: #1e293b !important;
        border-color: #334155 !important;
        color: #f1f5f9 !important;
      }
      html.dark .metric.dark {
        background-color: #020617 !important;
        border: 1px solid #334155 !important;
        color: #ffffff !important;
      }
      html.dark h1, html.dark h2, html.dark h3, html.dark h4,
      html.dark .heading, html.dark .title, html.dark .value,
      html.dark .text-slate-900:not([class*="dark:text-"]),
      html.dark .text-slate-800:not([class*="dark:text-"]),
      html.dark .text-gray-900:not([class*="dark:text-"]),
      html.dark .text-gray-800:not([class*="dark:text-"]) {
        color: #f8fafc !important;
      }
      html.dark .sub, html.dark .label, html.dark .kicker, html.dark .meta,
      html.dark .text-slate-600:not([class*="dark:text-"]),
      html.dark .text-slate-500:not([class*="dark:text-"]),
      html.dark .text-gray-600:not([class*="dark:text-"]),
      html.dark .text-gray-500:not([class*="dark:text-"]) {
        color: #94a3b8 !important;
      }
      html.dark input:not([type="checkbox"]):not([type="radio"]),
      html.dark select,
      html.dark textarea,
      html.dark .input,
      html.dark .select {
        background-color: #0f172a !important;
        border-color: #334155 !important;
        color: #f1f5f9 !important;
      }
      html.dark table th, html.dark thead, html.dark .table th {
        background-color: #0f172a !important;
        color: #94a3b8 !important;
        border-color: #334155 !important;
      }
      html.dark table td, html.dark tbody tr, html.dark .table td {
        border-color: #334155 !important;
        color: #e2e8f0 !important;
      }
    </style>
  `;

  // Inject a lightweight Rapider API wrapper into the iframe so custom scripts can securely proxy queries to the backend
  const rapiderSdkScript = `
    <script data-rapider-injected="true">
      (function() {
        const injectedRouteParams = ${JSON.stringify(routeParams || {})};
        const injectedSwrSeed = ${JSON.stringify(initialSwrCache || {})};
        const injectedProjectId = ${JSON.stringify(activeProjectId || '')};
        window.__rapiderInjectedProjectId = injectedProjectId;
        window.__rapiderSwrMemory = Object.assign(Object.create(null), injectedSwrSeed);

        function createMemoryStorage(initialEntries, onMutate) {
          const store = Object.create(null);
          if (initialEntries && typeof initialEntries === 'object') {
            Object.keys(initialEntries).forEach(function(k) {
              if (initialEntries[k] !== undefined && initialEntries[k] !== null) {
                store[k] = String(initialEntries[k]);
              }
            });
          }
          const api = {
            getItem: function(key) {
              const k = String(key);
              return Object.prototype.hasOwnProperty.call(store, k) ? store[k] : null;
            },
            setItem: function(key, value) {
              const k = String(key);
              const v = String(value);
              store[k] = v;
              if (onMutate) onMutate('set', k, v);
            },
            removeItem: function(key) {
              const k = String(key);
              if (Object.prototype.hasOwnProperty.call(store, k)) {
                delete store[k];
                if (onMutate) onMutate('remove', k, null);
              }
            },
            clear: function() {
              Object.keys(store).forEach(function(k) { delete store[k]; });
              if (onMutate) onMutate('clear', null, null);
            },
            key: function(index) {
              const keys = Object.keys(store);
              return index >= 0 && index < keys.length ? keys[index] : null;
            }
          };
          Object.defineProperty(api, 'length', {
            get: function() { return Object.keys(store).length; },
            enumerable: false,
            configurable: true
          });
          return new Proxy(api, {
            get: function(target, prop) {
              if (prop in target) {
                const val = target[prop];
                return typeof val === 'function' ? val.bind(target) : val;
              }
              if (typeof prop === 'string' && Object.prototype.hasOwnProperty.call(store, prop)) {
                return store[prop];
              }
              return undefined;
            },
            set: function(target, prop, value) {
              if (typeof prop === 'string' && !(prop in target)) {
                api.setItem(prop, value);
                return true;
              }
              target[prop] = value;
              return true;
            },
            deleteProperty: function(target, prop) {
              if (typeof prop === 'string' && Object.prototype.hasOwnProperty.call(store, prop)) {
                api.removeItem(prop);
                return true;
              }
              return true;
            },
            has: function(target, prop) {
              return (prop in target) || (typeof prop === 'string' && Object.prototype.hasOwnProperty.call(store, prop));
            },
            ownKeys: function() {
              return Object.keys(store);
            },
            getOwnPropertyDescriptor: function(target, prop) {
              if (typeof prop === 'string' && Object.prototype.hasOwnProperty.call(store, prop)) {
                return { value: store[prop], writable: true, enumerable: true, configurable: true };
              }
              return Object.getOwnPropertyDescriptor(target, prop);
            }
          });
        }

        // Install safe in-memory Storage & cookie shims when running inside an opaque sandbox (without allow-same-origin)
        try {
          const testSession = window.sessionStorage;
          void testSession.length;
        } catch (err) {
          const sessionShim = createMemoryStorage(injectedSwrSeed, function(action, key, value) {
            if (action === 'set' && key && (key.startsWith('swr_find_') || key.startsWith('swr_count_'))) {
              window.__rapiderSwrMemory[key] = value;
              window.parent.postMessage({ type: 'RAPIDER_SWR_SYNC', action: 'set', key: key, value: value }, '*');
            } else if (action === 'remove' && key && (key.startsWith('swr_find_') || key.startsWith('swr_count_'))) {
              delete window.__rapiderSwrMemory[key];
              window.parent.postMessage({ type: 'RAPIDER_SWR_SYNC', action: 'remove', key: key }, '*');
            } else if (action === 'clear') {
              window.__rapiderSwrMemory = Object.create(null);
              window.parent.postMessage({ type: 'RAPIDER_SWR_SYNC', action: 'clear' }, '*');
            }
          });
          try {
            Object.defineProperty(window, 'sessionStorage', { value: sessionShim, configurable: true, enumerable: true });
          } catch (_) {
            try {
              Object.defineProperty(Window.prototype, 'sessionStorage', { get: function() { return sessionShim; }, configurable: true });
            } catch (__) {}
          }
        }

        try {
          const testLocal = window.localStorage;
          void testLocal.length;
        } catch (err) {
          const localSeed = injectedProjectId ? { projectId: injectedProjectId, activeProjectId: injectedProjectId } : {};
          const localShim = createMemoryStorage(localSeed, null);
          try {
            Object.defineProperty(window, 'localStorage', { value: localShim, configurable: true, enumerable: true });
          } catch (_) {
            try {
              Object.defineProperty(Window.prototype, 'localStorage', { get: function() { return localShim; }, configurable: true });
            } catch (__) {}
          }
        }

        try {
          void document.cookie;
        } catch (err) {
          let memCookie = '';
          try {
            Object.defineProperty(document, 'cookie', {
              get: function() { return memCookie; },
              set: function(v) { memCookie = String(v || ''); },
              configurable: true
            });
          } catch (_) {}
        }

        try {
          if (!window.location.hash && window.parent && window.parent !== window && window.parent.location.hash) {
            window.location.hash = window.parent.location.hash;
          }
        } catch(e) {}

        const origGet = URLSearchParams.prototype.get;
        URLSearchParams.prototype.get = function(key) {
          const val = origGet.call(this, key);
          if (val !== null && val !== undefined) return val;
          if (injectedRouteParams && injectedRouteParams[key] !== undefined && injectedRouteParams[key] !== null) {
            return String(injectedRouteParams[key]);
          }
          return null;
        };
        const origHas = URLSearchParams.prototype.has;
        URLSearchParams.prototype.has = function(key) {
          if (origHas.call(this, key)) return true;
          return injectedRouteParams && injectedRouteParams[key] !== undefined && injectedRouteParams[key] !== null;
        };
      })();

      function modalConfigFromArgs(routeOrConfig, params) {
        if (!routeOrConfig) return null;
        if (Array.isArray(routeOrConfig)) {
          var r = routeOrConfig[0] ? String(routeOrConfig[0]) : '';
          var p = (routeOrConfig.length > 1 && routeOrConfig[1] && typeof routeOrConfig[1] === 'object') ? routeOrConfig[1] : (params || {});
          return r ? { route: r, payload: p } : null;
        }
        if (typeof routeOrConfig === 'string') {
          return { route: routeOrConfig, payload: params || {} };
        }
        if (typeof routeOrConfig === 'object') {
          var cfg = Object.assign({}, routeOrConfig);
          if (!cfg.payload && cfg.params) {
            cfg.payload = cfg.params;
          }
          return cfg;
        }
        return null;
      }

      window.rapiderApi = {
        apiLogs: [],
        // --- API REQUEST BROKER ---
        _inFlightMutations: {},
        clearCache: function(entityName) {
          try {
            if (window.__rapiderSwrMemory) {
              Object.keys(window.__rapiderSwrMemory).forEach(k => {
                if (!entityName || k.includes(entityName)) {
                  delete window.__rapiderSwrMemory[k];
                }
              });
            }
            Object.keys(window.sessionStorage).forEach(k => {
              if (!entityName || k.includes(entityName)) {
                if (k.startsWith('swr_find_') || k.startsWith('swr_count_')) {
                  window.sessionStorage.removeItem(k);
                }
              }
            });
          } catch(e) {
            window.parent.postMessage({ type: 'RAPIDER_SWR_SYNC', action: 'clearEntity', entityName: entityName || '' }, '*');
          }
        },
        request: function(operation, entityName, payload = {}, loggedOperation) {
          this.apiLogs.push({ operation: loggedOperation || operation, entityName: entityName, payload: payload, timestamp: Date.now() });
          const isMutation = ['create', 'update', 'updateById', 'delete', 'deleteById'].includes(operation);

          // E2E Test Mode Mutation Interception:
          // In test mode, we do NOT persist mutations to the real backend. We capture the exact payload
          // in apiLogs, clear the entity cache, broadcast data changes, and immediately resolve with a simulated
          // success response so test assertions can verify the payload without altering real records.
          if ((this.__testMode || window.__rapiderTestMode) && isMutation) {
            this.clearCache(entityName);
            this.broadcast('RAPIDER_DATA_CHANGED', { entityName: entityName, operation: operation });
            var mockId = payload.id || ('test_' + Math.random().toString(36).substr(2, 9));
            var mockBody = payload.body || {};
            var mockResponse = (operation === 'delete' || operation === 'deleteById')
              ? { count: 1, success: true, id: payload.id }
              : Object.assign({
                  id: mockId,
                  createdDate: new Date().toISOString(),
                  updatedDate: new Date().toISOString()
                }, mockBody);

            window.parent.postMessage({
              type: 'RAPIDER_API_RESPONSE',
              reqId: 'test_mock_' + Math.random().toString(36).substr(2, 9),
              payload: mockResponse,
              operation: operation,
              entityName: entityName,
              isTestMock: true
            }, '*');

            return Promise.resolve(mockResponse);
          }

          let mutationKey = null;

          if (isMutation) {
            mutationKey = operation + ':' + entityName + ':' + (payload.id || '') + ':' + JSON.stringify(payload.body || {});
            if (this._inFlightMutations[mutationKey]) {
              console.warn('[RapiderSDK] Duplicate mutation suppressed. Reusing active in-flight request:', mutationKey);
              return this._inFlightMutations[mutationKey];
            }
          }

          const reqPromise = new Promise((resolve, reject) => {
            const reqId = Math.random().toString(36).substr(2, 9);
            
            const handler = (event) => {
              if (event.data && event.data.reqId === reqId) {
                window.removeEventListener('message', handler);
                if (event.data.type === 'RAPIDER_API_RESPONSE') {
                  if (isMutation) {
                    this.clearCache(entityName);
                    this.broadcast('RAPIDER_DATA_CHANGED', { entityName, operation });
                  }
                  resolve(event.data.payload);
                } else if (event.data.type === 'RAPIDER_API_ERROR') {
                  reject(new Error(event.data.error));
                }
              }
            };
            
            window.addEventListener('message', handler);
            
            window.parent.postMessage({
              type: 'RAPIDER_API_REQUEST',
              reqId: reqId,
              operation: operation,
              entityName: entityName,
              payload: payload
            }, '*');
          });

          if (isMutation && mutationKey) {
            this._inFlightMutations[mutationKey] = reqPromise;
            reqPromise.finally(() => {
              delete this._inFlightMutations[mutationKey];
            });
          }

          return reqPromise;
        },
        create: function(entityName, body) { return this.request('create', entityName, { body }); },
        find: function(entityName, filter) { return this.request('find', entityName, { filter }); },
        showToast: function() {
          if (typeof document === 'undefined') return;
          let toast = document.getElementById('swr-toast');
          if (!toast) {
            toast = document.createElement('div');
            toast.id = 'swr-toast';
            toast.style.cssText = 'position:fixed;bottom:20px;right:20px;background:#172137;color:#fff;padding:12px 20px;border-radius:8px;font-size:13px;box-shadow:0 10px 25px rgba(0,0,0,0.2);display:flex;align-items:center;gap:10px;z-index:9999;transition:opacity 0.3s;opacity:0;pointer-events:none;';
            toast.innerHTML = '<i class="fas fa-sync fa-spin"></i> Checking for updates...';
            document.body.appendChild(toast);
            setTimeout(() => toast.style.opacity = '1', 10);
          }
        },
        hideToast: function() {
          if (typeof document === 'undefined') return;
          const toast = document.getElementById('swr-toast');
          if (toast) {
            toast.style.opacity = '0';
            setTimeout(() => toast.remove(), 300);
          }
        },
        findSWR: function(entityName, filter, callback) {
          let activeProjectId = window.__rapiderInjectedProjectId || '';
          try {
            activeProjectId = window.localStorage.getItem('projectId') || window.localStorage.getItem('activeProjectId') || activeProjectId;
          } catch(e) {}
          const cacheKey = 'swr_find_' + activeProjectId + '_' + entityName + '_' + JSON.stringify(filter || {});
          let cachedData = null;
          try {
            let cached = null;
            try { cached = window.sessionStorage.getItem(cacheKey); } catch(_) {}
            if (!cached && window.__rapiderSwrMemory && window.__rapiderSwrMemory[cacheKey]) {
              cached = window.__rapiderSwrMemory[cacheKey];
            }
            if (cached) {
              cachedData = JSON.parse(cached);
              this.showToast();
              if (callback) callback(cachedData, true);
            }
          } catch(e) {
            console.error('[SWR GET ERROR]', e);
          }
          
          const reqPromise = this.request('find', entityName, { filter }, 'findSWR').then(data => {
            const serialized = JSON.stringify(data);
            if (window.__rapiderSwrMemory) window.__rapiderSwrMemory[cacheKey] = serialized;
            try { 
              window.sessionStorage.setItem(cacheKey, serialized); 
            } catch(e) {
              window.parent.postMessage({ type: 'RAPIDER_SWR_SYNC', action: 'set', key: cacheKey, value: serialized }, '*');
            }
            if (cachedData !== null) this.hideToast();
            if (callback) callback(data, false);
            return data;
          }).catch(err => {
            if (cachedData !== null) this.hideToast();
            throw err;
          });
          /* 
            CRITICAL SWR CACHE RETURN EXPLANATION:
            We intentionally return Promise.resolve(cachedData) if cache exists, REGARDLESS of whether 
            a callback was provided. 
            
            Historically, we restricted this to \`if (cachedData && callback)\` because returning cached 
            data for an \`await\` without a callback permanently freezes the UI with stale data (since 
            the await resolves immediately and cannot resolve again when fresh network data arrives).
            
            HOWEVER, many pages (like the CRM dashboard) rely on \`const data = await window.rapiderApi.findSWR(...)\`.
            By returning cache immediately, we allow the page to render instantly with stale data. 
            The background request (\`reqPromise\`) still executes and silently updates the cache in 
            sessionStorage. The next time the user navigates to the page, they will see the new data instantly.
            
            DO NOT add \`&& callback\` back to this condition. Doing so will cause pages that use \`await findSWR\`
            to ignore the cache entirely, causing the UI to show a blocking loading state on every navigation.
          */
          if (cachedData !== null) {
             return Promise.resolve(cachedData);
          }
          return reqPromise;
        },
        countSWR: function(entityName, where, callback) {
          let activeProjectId = window.__rapiderInjectedProjectId || '';
          try {
            activeProjectId = window.localStorage.getItem('projectId') || window.localStorage.getItem('activeProjectId') || activeProjectId;
          } catch(e) {}
          const cacheKey = 'swr_count_' + activeProjectId + '_' + entityName + '_' + JSON.stringify(where || {});
          let cachedData = null;
          try {
            let cached = null;
            try { cached = window.sessionStorage.getItem(cacheKey); } catch(_) {}
            if (!cached && window.__rapiderSwrMemory && window.__rapiderSwrMemory[cacheKey]) {
              cached = window.__rapiderSwrMemory[cacheKey];
            }
            if (cached) {
              cachedData = JSON.parse(cached);
              this.showToast();
              if (callback) callback(cachedData, true);
            }
          } catch(e) {
            console.error('[SWR GET ERROR]', e);
          }
          
          const reqPromise = this.request('count', entityName, { where }, 'countSWR').then(data => {
            const serialized = JSON.stringify(data);
            if (window.__rapiderSwrMemory) window.__rapiderSwrMemory[cacheKey] = serialized;
            try { 
              window.sessionStorage.setItem(cacheKey, serialized); 
            } catch(e) {
              window.parent.postMessage({ type: 'RAPIDER_SWR_SYNC', action: 'set', key: cacheKey, value: serialized }, '*');
            }
            if (cachedData !== null) this.hideToast();
            if (callback) callback(data, false);
            return data;
          }).catch(err => {
            if (cachedData !== null) this.hideToast();
            throw err;
          });
          
          if (cachedData !== null) {
             return Promise.resolve(cachedData);
          }
          return reqPromise;
        },
        findById: function(entityName, id, filter) { return this.request('findById', entityName, { id, filter }); },
        updateById: function(entityName, id, body) { return this.request('updateById', entityName, { id, body }); },
        update: function(entityName, id, body) { return this.request('updateById', entityName, { id, body }); },
        deleteById: function(entityName, id) { return this.request('deleteById', entityName, { id }); },
        delete: function(entityName, id) { return this.request('deleteById', entityName, { id }); },
        count: function(entityName, where) { return this.request('count', entityName, { where }); },
        clearCache: function(entityName) {
          const target = entityName ? String(entityName).toLowerCase() : '';
          try {
            if (window.__rapiderSwrMemory) {
              Object.keys(window.__rapiderSwrMemory).forEach(function(k) {
                if (!target || k.toLowerCase().includes('_' + target + '_')) {
                  delete window.__rapiderSwrMemory[k];
                }
              });
            }
          } catch (_) {}
          try {
            if (window.sessionStorage) {
              Object.keys(window.sessionStorage).forEach(function(k) {
                if (k.startsWith('swr_find_') || k.startsWith('swr_count_')) {
                  if (!target || k.toLowerCase().includes('_' + target + '_')) {
                    window.sessionStorage.removeItem(k);
                  }
                }
              });
            }
          } catch (_) {}
          try {
            window.parent.postMessage({
              type: 'RAPIDER_SWR_SYNC',
              action: entityName ? 'clearEntity' : 'clear',
              entityName: entityName || undefined
            }, '*');
          } catch (_) {}
        },
        triggerWorkflow: function(workflowName, payload) { return this.request('triggerWorkflow', workflowName, { name: workflowName, payload: payload || {} }, 'triggerWorkflow'); },

        // --- CROSS-IFRAME EVENT BUS ---
        listeners: {},
        
        // Broadcasts an event to all other iframe sandbox instances
        broadcast: function(eventName, payload) {
          window.parent.postMessage({
            type: 'RAPIDER_BROADCAST',
            eventName: eventName,
            payload: payload
          }, '*');
        },
        
        // Registers a listener for broadcasts from other iframes
        on: function(eventName, callback) {
          if (!this.listeners[eventName]) this.listeners[eventName] = [];
          this.listeners[eventName].push(callback);
        },

        // --- ROUTING API ---
        routeParams: ${JSON.stringify(routeParams || {})},
        getRouteParam: function(key) {
          if (this.routeParams && this.routeParams[key] !== undefined && this.routeParams[key] !== null) {
            return this.routeParams[key];
          }
          try {
            const urlParams = new URLSearchParams(window.location.search);
            if (urlParams.has(key)) return urlParams.get(key);
            const hash = window.location.hash || '';
            if (hash.includes('?')) {
              const hashParams = new URLSearchParams(hash.split('?')[1]);
              if (hashParams.has(key)) return hashParams.get(key);
            }
          } catch(e) {}
          return null;
        },

        // --- UI ACTIONS & NAVIGATION BROKER ---
        
        /**
         * @method executeAction
         * @description Generic dispatcher that formats a UI action request and posts it securely to the parent Angular wrapper.
         * @param {Object} actionConfig - The JSON payload defining the action type and nested parameters.
         */
        executeAction: function(actionConfig) {
          if (
            actionConfig &&
            actionConfig.type === 'navigate' &&
            (window.__rapiderCommentModeActive ||
              document.documentElement.classList.contains('rapider-comment-mode') ||
              (document.body && document.body.classList.contains('rapider-comment-mode')))
          ) {
            return;
          }
          window.parent.postMessage({
            type: 'RAPIDER_UI_ACTION',
            action: actionConfig
          }, '*');
        },

        /**
         * @method showNotification
         * @description Triggers the global Toast notification service in the parent app.
         * @param {Object} notificationConfig - e.g. { type: 'success', message: 'Done!' }
         */
        showNotification: function(notificationConfig) {
          this.executeAction({ type: 'showNotification', payload: { notification: notificationConfig } });
        },

        /**
         * @method navigate
         * @description Triggers the parent Angular Router to navigate to a new view.
         * @param {string|Array} route - The target path e.g. '/database/123' or ['lead-detail', id]
         * @param {Object} [payload] - Optional navigation payload or queryParams
         */
        navigate: function(route, payload) {
          if (
            window.__rapiderCommentModeActive ||
            document.documentElement.classList.contains('rapider-comment-mode') ||
            (document.body && document.body.classList.contains('rapider-comment-mode'))
          ) {
            return;
          }
          const navPayload = typeof payload === 'object' && payload !== null ? Object.assign({ route: route }, payload) : { route: route };
          this.executeAction({ type: 'navigate', payload: navPayload });
        },

        /**
         * @method showPageModal
         * @description Opens an NG-Zorro modal overlaying the current view with a dynamic nested page.
         * @param {Object} modalConfig - e.g. { route: 'user-details', title: 'Details', width: 800 }
         */
        showPageModal: function(modalConfig) {
          this.executeAction({ type: 'showPageModal', payload: { pageModal: modalConfig } });
        },

        openPageModal: function(routeOrConfig, params) {
          var cfg = modalConfigFromArgs(routeOrConfig, params);
          if (cfg) {
            this.showPageModal(cfg);
          }
        },

        /**
         * @method closePageModal
         * @description Closes the active page modal.
         */
        closePageModal: function() {
          this.executeAction({ type: 'closePageModal' });
        },

        /**
         * @method showPageDrawer
         * @description Opens an NG-Zorro drawer sliding from the edge, projecting a nested page.
         * @param {Object} drawerConfig - e.g. { route: 'settings', placement: 'right', size: 'large' }
         */
        showPageDrawer: function(drawerConfig) {
          this.executeAction({ type: 'showPageDrawer', payload: { pageDrawer: drawerConfig } });
        },

        /**
         * @method openPageDrawer
         * @description Alias for showPageDrawer supporting array tuple (['route', params]), (route, params), or config object.
         */
        openPageDrawer: function(routeOrConfig, params) {
          var cfg = modalConfigFromArgs(routeOrConfig, params);
          if (cfg) {
            this.showPageDrawer(cfg);
          }
        },

        /**
         * @method closePageDrawer
         * @description Closes the active page drawer.
         */
        closePageDrawer: function() {
          this.executeAction({ type: 'closePageDrawer' });
        },

        /**
         * @method showPageSplitter
         * @description Transforms the parent layout into an NG-Zorro split-pane containing a dynamic child page.
         * @param {Object} splitterConfig - Configures sizing, layout orientation, route, and titles.
         */
        showPageSplitter: function(splitterConfig) {
          this.executeAction({ type: 'showPageSplitter', payload: { pageSplitter: splitterConfig } });
        },

        /**
         * @method showConfirmModal
         * @description Displays a stylized confirmation modal dialog.
         * @param {Object} options - { title, message, okText, cancelText, isDanger, onConfirm, onCancel }
         */
        showConfirmModal: function(options) {
          if (typeof window.showConfirmModal === 'function') {
            window.showConfirmModal(options);
          }
        }
      };

      // --- GLOBAL MODAL CONFIRMATION DIALOG & ALERT OVERRIDE ---
      window.showConfirmModal = function(options) {
        options = options || {};
        var modal = document.getElementById('rapiderGlobalConfirmModal');
        if (!modal) {
          modal = document.createElement('div');
          modal.id = 'rapiderGlobalConfirmModal';
          modal.className = 'fixed inset-0 z-[99999] flex items-center justify-center bg-black/60 backdrop-blur-sm transition-opacity duration-200';
          modal.setAttribute('role', 'dialog');
          modal.setAttribute('aria-modal', 'true');
          modal.innerHTML = '' +
            '<div class="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl border border-slate-200/80 dark:border-slate-800 p-6 max-w-md w-full mx-4 transform transition-all scale-100">' +
              '<div class="flex items-start gap-4">' +
                '<div id="rapiderConfirmIconContainer" class="w-12 h-12 rounded-xl flex items-center justify-center shrink-0">' +
                  '<i id="rapiderConfirmIcon" class="fas fa-trash-alt text-xl"></i>' +
                '</div>' +
                '<div class="flex-1 min-w-0">' +
                  '<h3 id="rapiderConfirmTitle" class="text-lg font-bold text-slate-900 dark:text-white leading-snug">Confirm Action</h3>' +
                  '<p id="rapiderConfirmMessage" class="mt-2 text-sm text-slate-600 dark:text-slate-400 leading-relaxed"></p>' +
                '</div>' +
              '</div>' +
              '<div class="mt-6 flex items-center justify-end gap-3 pt-2">' +
                '<button id="rapiderConfirmCancelBtn" type="button" class="px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl transition-colors border border-slate-200 dark:border-slate-700 cursor-pointer">' +
                  'Cancel' +
                '</button>' +
                '<button id="rapiderConfirmOkBtn" type="button" style="color: #ffffff !important;" class="px-4 py-2 text-sm font-semibold !text-white text-white rounded-xl shadow-sm transition-all focus:outline-none focus:ring-2 focus:ring-offset-2 cursor-pointer">' +
                  'Confirm' +
                '</button>' +
              '</div>' +
            '</div>';
          document.body.appendChild(modal);
        }

        var titleEl = modal.querySelector('#rapiderConfirmTitle');
        var msgEl = modal.querySelector('#rapiderConfirmMessage');
        var okBtn = modal.querySelector('#rapiderConfirmOkBtn');
        var cancelBtn = modal.querySelector('#rapiderConfirmCancelBtn');
        var iconContainer = modal.querySelector('#rapiderConfirmIconContainer');
        var iconEl = modal.querySelector('#rapiderConfirmIcon');

        titleEl.textContent = options.title || 'Confirm Action';
        msgEl.textContent = options.message || 'Are you sure you want to proceed?';
        okBtn.textContent = options.okText || (options.isDanger ? 'Delete' : 'Confirm');
        cancelBtn.textContent = options.cancelText || 'Cancel';

        if (options.isDanger !== false) {
          iconContainer.className = 'w-12 h-12 rounded-xl flex items-center justify-center shrink-0 bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400';
          iconEl.className = 'fas fa-exclamation-triangle text-xl';
          okBtn.className = 'px-4 py-2 text-sm font-semibold !text-white text-white bg-red-600 hover:bg-red-700 focus:ring-red-500 rounded-xl shadow-sm hover:shadow transition-all cursor-pointer';
          okBtn.style.setProperty('color', '#ffffff', 'important');
        } else {
          iconContainer.className = 'w-12 h-12 rounded-xl flex items-center justify-center shrink-0 bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400';
          iconEl.className = 'fas fa-question-circle text-xl';
          okBtn.className = 'px-4 py-2 text-sm font-semibold !text-white text-white bg-blue-600 hover:bg-blue-700 focus:ring-blue-500 rounded-xl shadow-sm hover:shadow transition-all cursor-pointer';
          okBtn.style.setProperty('color', '#ffffff', 'important');
        }

        modal.classList.remove('hidden');

        function cleanup() {
          modal.classList.add('hidden');
          okBtn.onclick = null;
          cancelBtn.onclick = null;
        }

        cancelBtn.onclick = function() {
          cleanup();
          if (typeof options.onCancel === 'function') options.onCancel();
        };

        okBtn.onclick = function() {
          cleanup();
          if (typeof options.onConfirm === 'function') {
            try {
              var res = options.onConfirm();
              if (res && typeof res.catch === 'function') {
                res.catch(function(err) {
                  console.error('[showConfirmModal] onConfirm error:', err);
                });
              }
            } catch (err) {
              console.error('[showConfirmModal] onConfirm error:', err);
            }
          }
        };
      };
      window.rapiderApi.showConfirmModal = window.showConfirmModal;

      // Safe Interceptor: Block Native Browser Alert & Toastify Instead
      window.alert = function(message) {
        console.warn('[Rapider] Blocked native alert():', message);
        if (window.rapiderApi && window.rapiderApi.showNotification) {
          window.rapiderApi.showNotification({
            type: 'warning',
            message: String(message)
          });
        }
      };


      // --- NATIVE WINDOW LISTENERS ---
      window.addEventListener('hashchange', function(event) {
        if (
          window.__rapiderCommentModeActive ||
          document.documentElement.classList.contains('rapider-comment-mode') ||
          (document.body && document.body.classList.contains('rapider-comment-mode'))
        ) {
          return;
        }
        let hash = window.location.hash;
        if (hash.startsWith('#/')) hash = hash.substring(2);
        else if (hash.startsWith('#')) hash = hash.substring(1);
        
        if (hash) {
           const path = hash.split('?')[0];
           const queryString = hash.includes('?') ? hash.split('?')[1] : '';
           let queryParams = {};
           if (queryString) {
             const searchParams = new URLSearchParams(queryString);
             for (const [key, value] of searchParams.entries()) {
               queryParams[key] = value;
             }
           }
           window.rapiderApi.executeAction({ 
             type: 'navigate', 
             payload: { route: path, queryParams: queryParams } 
           });
        }
      });

      // CRITICAL FIX: Intercept anchor clicks because <base> tag hijacks href="#..." and relative links (e.g. roster.html, /roster)
      // causing full page navigations to the base URL (S3/CDN bucket or /assets/) which breaks the wrapper.
      document.addEventListener('click', function(e) {
        if (
          window.__rapiderCommentModeActive ||
          document.documentElement.classList.contains('rapider-comment-mode') ||
          (document.body && document.body.classList.contains('rapider-comment-mode'))
        ) {
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          return;
        }
        const anchor = e.target.closest('a');
        if (!anchor) return;
        const rawHref = anchor.getAttribute('href');
        if (!rawHref) return;
        if (rawHref.startsWith('mailto:') || rawHref.startsWith('tel:') || rawHref.startsWith('javascript:') || anchor.hasAttribute('download')) return;

        // Allow genuine external links unless they accidentally point to a Rapider CDN/localhost app HTML asset
        const isRapiderAssetHtmlUrl = new RegExp('^https?://[^/]*(digitaloceanspaces\\\\.com|localhost|127\\\\.0\\\\.0\\\\.1)[^?]*\\\\.html(\\\\?.*|#.*)?$', 'i').test(rawHref);
        if ((rawHref.startsWith('http://') || rawHref.startsWith('https://')) && !isRapiderAssetHtmlUrl) return;
        if (anchor.getAttribute('target') === '_blank' && !isRapiderAssetHtmlUrl) return;

        // Ignore in-page skip links (e.g. href="#main") if matching element exists on page
        if (rawHref.startsWith('#') && !rawHref.startsWith('#/') && rawHref.length > 1) {
          try {
            if (document.querySelector(rawHref)) return;
          } catch (_) {}
        }

        // Internal navigation link
        e.preventDefault(); // Stop native navigation
        e.stopPropagation();

        let target = rawHref;
        if (isRapiderAssetHtmlUrl) {
          try {
            const parsedUrl = new URL(rawHref);
            target = parsedUrl.pathname + parsedUrl.search;
          } catch (_) {}
        }

        if (target.startsWith('#/')) target = target.slice(2);
        else if (target.startsWith('#')) target = target.slice(1);
        else if (target.startsWith('./')) target = target.slice(2);
        else if (target.startsWith('/')) target = target.slice(1);

        const parts = target.split('?');
        let route = parts[0].split('#')[0].replace(/\\.html$/i, '');
        if (route.includes('/')) {
          const segments = route.split('/').filter(Boolean);
          route = segments[segments.length - 1] || route;
        }
        let queryParams = {};
        if (parts[1]) {
          const searchParams = new URLSearchParams(parts[1].split('#')[0]);
          for (const [key, value] of searchParams.entries()) {
            queryParams[key] = value;
          }
        }

        window.rapiderApi.executeAction({
          type: 'navigate',
          payload: { route: route, queryParams: queryParams }
        });
      }, true);

      window.addEventListener('message', function(event) {
        if (!event.data) return;

        // 1. Listen for dynamic dark mode toggles from the parent window
        if (event.data.type === 'TOGGLE_DARK_MODE') {
          if (event.data.isDark) {
            document.documentElement.classList.add('dark');
          } else {
            document.documentElement.classList.remove('dark');
          }
        }

        // 2. Listen for Cross-Iframe Broadcasts forwarded by the parent Angular app
        if (event.data.type === 'RAPIDER_BROADCAST_RECEIVE') {
          const callbacks = window.rapiderApi.listeners[event.data.eventName] || [];
          callbacks.forEach(cb => cb(event.data.payload));
        }

        // 3. Listen for targeted SWR cache invalidations from parent Angular app
        if (event.data.type === 'RAPIDER_CLEAR_SWR_CACHE') {
          const entities = Array.isArray(event.data.entities) ? event.data.entities : [];
          if (entities.length === 0) {
            window.rapiderApi.clearCache();
          } else {
            entities.forEach(ent => window.rapiderApi.clearCache(ent));
          }
        }

        // 4. Listen for live application theme switches (e.g. Default vs Obsidian Compact)
        if (event.data.type === 'SET_APP_THEME') {
          var nextThemeKey = String(event.data.themeKey || 'default');
          var nextThemeCss = typeof event.data.themeCss === 'string' ? event.data.themeCss : '';
          document.documentElement.setAttribute('data-rapider-theme', nextThemeKey);
          var themeStyleEl = document.getElementById('rapider-active-app-theme');
          if (!themeStyleEl) {
            themeStyleEl = document.createElement('style');
            themeStyleEl.id = 'rapider-active-app-theme';
            themeStyleEl.setAttribute('data-rapider-injected', 'true');
            (document.head || document.documentElement).appendChild(themeStyleEl);
          }
          themeStyleEl.setAttribute('data-theme-key', nextThemeKey);
          themeStyleEl.textContent = nextThemeCss;
        }
      });
    </script>
  `;

  const activeThemeStyleTag = `<style data-rapider-injected="true" id="rapider-active-app-theme" data-theme-key="${safeThemeKey}">\n${appThemeCss || ''}\n</style>`;

  // Inject assets if it's missing a head/html tag structure
  if (!/<head(\s|>)/i.test(completeHtml)) {
    completeHtml = `
      <!DOCTYPE html>
       <html ${activeClassMode} data-rapider-theme="${safeThemeKey}">
        <head>
          <meta charset="utf-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          ${injectedHeadAssets}
          ${rapiderSdkScript}
          <style>
            html, body {
              margin: 0;
              padding: 0;
              font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
              background-color: var(--color-body-background, #f9fbfd);
              color: var(--color-text-main, #343a40);
              min-height: 100vh;
            }
            .dark html, .dark body, html.dark body {
              background-color: var(--color-body-background, #1f2126);
              color: var(--color-text-main, #f0f4f8);
            }
          </style>
          ${activeThemeStyleTag}
        </head>
        <body>
          ${completeHtml}
        </body>
      </html>
    `;
  } else {
    // Inject base tag, SDK, and CDN assets right at the start of <head> so window.rapiderApi
    // and <base> are ready before any inline <head> or <body> scripts/links execute.
    // Use function replacer so '$' inside scripts/styles never triggers String.replace patterns.
    completeHtml = completeHtml.replace(
      /<head(\s[^>]*)?>/i,
      (match) => `${match}\n${injectedHeadAssets}\n${rapiderSdkScript}`,
    );

    // Inject active application theme override right before </head> so it cleanly overrides
    // page-level stylesheets (e.g. crm-expenses-design.css, crm-design-v3.css, inline :root blocks)
    // with ZERO HTML changes required.
    if (/<\/head>/i.test(completeHtml)) {
      completeHtml = completeHtml.replace(
        /<\/head>/i,
        (match) => `${activeThemeStyleTag}\n${match}`,
      );
    } else {
      completeHtml = `${activeThemeStyleTag}\n${completeHtml}`;
    }

    // Inject the dark mode toggle and data-rapider-theme attribute on <html>
    if (activeClassMode) {
      if (completeHtml.includes('<html')) {
        if (!completeHtml.includes('class="dark"')) {
          completeHtml = completeHtml.replace(
            '<html',
            `<html ${activeClassMode} data-rapider-theme="${safeThemeKey}"`,
          );
        } else {
          completeHtml = completeHtml.replace(
            '<html',
            `<html data-rapider-theme="${safeThemeKey}"`,
          );
        }
      } else {
        completeHtml = `<html ${activeClassMode} data-rapider-theme="${safeThemeKey}">\n${completeHtml}\n</html>`;
      }
    } else {
      completeHtml = completeHtml.replace(
        /<html[^>]*class=["'][^"']*dark[^"']*["'][^>]*>/i,
        (match) => match.replace('dark', '').trim(),
      );
      if (completeHtml.includes('<html')) {
        completeHtml = completeHtml.replace(
          '<html',
          `<html data-rapider-theme="${safeThemeKey}"`,
        );
      }
    }
  }

  return completeHtml;
}

/**
 * @class HtmlPreviewComponent
 * @description A standalone UI component designed to safely render raw, AI-generated HTML/JS snippets
 * inside an isolated iframe sandbox. Uses Blob URLs to bypass cross-origin restrictions while
 * preserving strict script constraints.
 */
@Component({
  selector: 'rapider-html-preview',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './html-preview.component.html',
  host: { class: 'w-full h-full flex-1 min-h-0 flex flex-col' },
})
export class HtmlPreviewComponent implements OnInit, OnDestroy {
  /** Angular's DomSanitizer is required to tell Angular to trust our dynamically generated Blob URL. */
  private readonly sanitizer = inject(DomSanitizer);

  /** Native DOM reference to the iframe to allow sending messages into it. */
  @ViewChild('sandboxIframe')
  public iframeElement!: ElementRef<HTMLIFrameElement>;

  /** Input Signal: The raw AI-generated string containing HTML, Tailwind, and inline <script> tags. */
  public rawCode = input<string>('');

  /** Input Signal: Optional title to display in the sandbox header. */
  public sandboxTitle = input<string>('ai-sandbox-preview.local');

  /** Input Signal: If true, injects the Rapider Content Editor SDK to enable native drag, drop, hover, and click interception inside the sandbox. */
  public enableEditorSdk = input<boolean>(false);

  /** Input Signal: Explicitly enables allow-same-origin ONLY when parent developer tools (Builder, Bug Pinning, E2E Runner) need DOM inspection. Defaults to false for strict origin isolation. */
  public allowSameOrigin = input<boolean>(false);

  /** Computed: Determines whether the iframe requires same-origin DOM access. */
  public effectiveAllowSameOrigin = computed(
    () => this.allowSameOrigin() || this.enableEditorSdk(),
  );

  /** Input Signal: Passes routing parameters down to the sandbox to be accessed via window.rapiderApi.getRouteParam() */
  public routeParams = input<Record<string, string>>({});

  /** Input Signal: Allows setting a base URL to resolve /assets/ references inside the raw HTML. Useful when the HTML is hosted in a builder iframe but needs assets from a specific app directory or CDN. */
  public assetBaseUrl = input<string>('');

  /** Input Signal: Active semantic application theme key (e.g. 'default', 'obsidian-compact'). */
  public appTheme = input<string>('default');

  /**
   * @property {InputSignal<boolean>} displayBrowserHeader
   * @description Local input flag controlling structural evaluation steps for the decorative mock browser layout window header.
   */
  public displayBrowserHeader = input<boolean>(true);

  /** Signal holding the sanitized Blob URL safely mapped to the iframe [src] when same-origin inspection is required. */
  public iframeUrl = signal<SafeResourceUrl | null>(null);

  /** Signal holding the sanitized HTML mapped to [srcdoc] when running in isolated null-origin sandbox mode. */
  public iframeSrcdoc = signal<SafeHtml | null>(null);

  /** Output event that broadcasts messages received from the internal iframe Javascript up to the parent wrapper. */
  public onIframeMessage = output<MessageEvent>();

  /** Output event indicating the iframe has finished loading its blob document. */
  public onIframeLoad = output<HTMLIFrameElement>();

  /** Cache the theme CSS to avoid repeated network requests */
  private static cachedThemeCss: string | null = null;

  /** Cache resolved per-theme CSS overrides (e.g. theme-default.css, theme-obsidian-compact.css) */
  private static readonly cachedAppThemeCss = new Map<string, string>();

  /** Observer to watch for theme changes on the parent document */
  private themeObserver: MutationObserver | null = null;

  /** Monotonic counter used to trigger an explicit sandbox reload when underlying model data mutates */
  private readonly refreshNonce = signal<number>(0);

  /**
   * Intercepts messages posted from the iframe to the parent window.
   * @param {MessageEvent} event - The message event payload.
   */
  @HostListener('window:message', ['$event'])
  public handleWindowMessage(event: MessageEvent): void {
    if (!event.data) return;

    const isFromThisIframe =
      this.iframeElement?.nativeElement?.contentWindow === event.source;

    // Safely persist/invalidate ONLY swr_find_* and swr_count_* cache entries from opaque null-origin sandboxes
    if (isFromThisIframe && event.data.type === 'RAPIDER_SWR_SYNC') {
      try {
        if (typeof sessionStorage !== 'undefined') {
          const { action, key, value, entityName } = event.data;
          const isValidSwrKey =
            typeof key === 'string' &&
            (key.startsWith('swr_find_') || key.startsWith('swr_count_'));

          if (action === 'set' && isValidSwrKey && typeof value === 'string') {
            sessionStorage.setItem(key, value);
          } else if (action === 'remove' && isValidSwrKey) {
            sessionStorage.removeItem(key);
          } else if (action === 'clear' || action === 'clearEntity') {
            Object.keys(sessionStorage).forEach((k) => {
              if (k.startsWith('swr_find_') || k.startsWith('swr_count_')) {
                if (!entityName || k.includes(String(entityName))) {
                  sessionStorage.removeItem(k);
                }
              }
            });
          }
        }
      } catch {
        // Ignore sessionStorage quota or access errors
      }
      return;
    }

    // Cross-iframe broadcasts must be received by all preview wrappers
    const isBroadcast = event.data.type === 'RAPIDER_BROADCAST';

    // Close actions must be received by parent wrappers to close open drawers/modals/splitters
    const isCloseAction =
      event.data.type === 'RAPIDER_UI_ACTION' &&
      (event.data.action?.type === 'closePageDrawer' ||
        event.data.action?.type === 'closePageModal' ||
        event.data.action?.type === 'closePageSplitter');

    // Only process messages originating from this component's iframe,
    // except for cross-iframe broadcasts and drawer/modal close actions.
    if (isFromThisIframe || isBroadcast || isCloseAction) {
      this.onIframeMessage.emit(event);
    }
  }

  constructor() {
    // Reactively regenerate the sandbox document whenever inputs or sandbox origin mode change
    effect((onCleanup) => {
      const code = this.rawCode();
      const params = this.routeParams();
      const assetBase = this.assetBaseUrl();
      const allowSame = this.effectiveAllowSameOrigin();
      const nonce = this.refreshNonce();

      if (code) {
        this.generateSandboxUrl(
          code,
          params,
          assetBase,
          allowSame,
          nonce,
          onCleanup,
        );
      } else {
        this.iframeUrl.set(null);
        this.iframeSrcdoc.set(null);
      }
    });

    // Hot-swap the active application theme inside the live iframe without losing page state
    effect(() => {
      const requestedTheme = normalizeAppThemeKey(this.appTheme());
      const assetBase = this.assetBaseUrl();
      void this.applyThemeToLiveIframe(requestedTheme, assetBase);
    });
  }

  private async resolveAppThemeCss(
    themeKey: string,
    assetBaseUrl: string,
  ): Promise<string> {
    const normalizedKey = normalizeAppThemeKey(themeKey);
    const themeFileName = `theme-${normalizedKey}.css`;
    const candidates: Array<{ cacheKey: string; url: string }> = [];

    if (assetBaseUrl && isTrustedAppResourceUrl(assetBaseUrl)) {
      let base = assetBaseUrl.replace(/\/$/, '');
      if (base.endsWith('/assets')) {
        base = base.slice(0, -7);
      }
      candidates.push({
        cacheKey: `${base}::${normalizedKey}`,
        url: `${base}/assets/themes/${themeFileName}`,
      });
    }
    candidates.push({
      cacheKey: `host::${normalizedKey}`,
      url: `/assets/themes/${themeFileName}`,
    });

    for (const candidate of candidates) {
      if (HtmlPreviewComponent.cachedAppThemeCss.has(candidate.cacheKey)) {
        return (
          HtmlPreviewComponent.cachedAppThemeCss.get(candidate.cacheKey) || ''
        );
      }
      try {
        const res = await fetch(candidate.url);
        if (res.ok) {
          const cssText = await res.text();
          // Guard against SPA index.html fallback when a static file does not exist
          if (!/^\s*<!doctype html/i.test(cssText) && !/^\s*<html/i.test(cssText)) {
            HtmlPreviewComponent.cachedAppThemeCss.set(
              candidate.cacheKey,
              cssText,
            );
            return cssText;
          }
        }
      } catch {
        // Fall through to host theme asset
      }
    }
    return '';
  }

  private async applyThemeToLiveIframe(
    themeKey: string,
    assetBaseUrl: string,
  ): Promise<void> {
    const normalizedKey = normalizeAppThemeKey(themeKey);
    const themeCss =
      normalizedKey === 'default'
        ? ''
        : await this.resolveAppThemeCss(normalizedKey, assetBaseUrl);
    this.postMessageToIframe({
      type: 'SET_APP_THEME',
      themeKey: normalizedKey,
      themeCss,
    });
  }

  ngOnInit() {
    // Watch the parent <html> tag for dark mode class toggles and sync it to the iframe natively
    if (typeof document !== 'undefined') {
      const htmlElement = document.documentElement;

      this.themeObserver = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.attributeName === 'class') {
            const isDark = htmlElement.classList.contains('dark');
            this.postMessageToIframe({ type: 'TOGGLE_DARK_MODE', isDark });
          }
        });
      });

      this.themeObserver.observe(htmlElement, { attributes: true });
    }
  }

  ngOnDestroy() {
    if (this.themeObserver) {
      this.themeObserver.disconnect();
    }
  }

  /**
   * Fetches the theme CSS and generates the secure iframe blob URL.
   * Uses async logic gracefully within the effect lifecycle.
   */
  private async generateSandboxUrl(
    code: string,
    params: Record<string, string>,
    assetBaseUrl: string,
    allowSameOrigin: boolean,
    nonce: number,
    onCleanup: (cleanupFn: () => void) => void,
  ) {
    let isCancelled = false;
    let blobUrl: string | null = null;

    onCleanup(() => {
      isCancelled = true;
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl);
      }
    });

    let themeCss = HtmlPreviewComponent.cachedThemeCss || '';

    // Fetch the theme file so we can inject its @theme configuration directly into the Tailwind CDN
    if (!themeCss) {
      try {
        const response = await fetch(
          '/assets/themes/rapider-tailwind-theme.css',
        );
        if (response.ok) {
          themeCss = await response.text();
          HtmlPreviewComponent.cachedThemeCss = themeCss;
        }
      } catch (e) {
        console.warn(
          '[HtmlPreviewComponent] Failed to load rapider-tailwind-theme.css',
          e,
        );
      }
    }

    if (isCancelled) return;

    // Seed SWR cache & activeProjectId ONLY (never tokens/secrets) when running in isolated null-origin mode
    let activeProjectId = '';
    const initialSwrCache: Record<string, string> = {};
    try {
      if (typeof localStorage !== 'undefined') {
        activeProjectId =
          localStorage.getItem('projectId') ||
          localStorage.getItem('activeProjectId') ||
          '';
      }
      if (!allowSameOrigin && typeof sessionStorage !== 'undefined') {
        Object.keys(sessionStorage).forEach((k) => {
          if (k.startsWith('swr_find_') || k.startsWith('swr_count_')) {
            const v = sessionStorage.getItem(k);
            if (v !== null) {
              initialSwrCache[k] = v;
            }
          }
        });
      }
    } catch {
      // Ignore storage read errors
    }

    const activeAppThemeKey = normalizeAppThemeKey(
      untracked(() => this.appTheme()),
    );
    const activeAppThemeCss =
      activeAppThemeKey === 'default'
        ? ''
        : await this.resolveAppThemeCss(activeAppThemeKey, assetBaseUrl);

    if (isCancelled) return;

    // 1. Build complete HTML natively
    let completeHtml = buildSandboxHtml(
      code,
      themeCss,
      undefined,
      params,
      assetBaseUrl,
      initialSwrCache,
      activeProjectId,
      activeAppThemeKey,
      activeAppThemeCss,
    );

    // 2. Inject Content Editor SDK if Builder Mode is enabled
    if (this.enableEditorSdk()) {
      const editorSdkScript = `
        <script data-rapider-injected="true">
          window.rapiderEditorApi = {
            init: function() {
              // CRITICAL ARCHITECTURE UPDATE:
              // Native click, mousemove, and mouseout listeners have been COMPLETELY REMOVED from the internal SDK.
              // They are now entirely managed natively by the Angular DragDropBuilderComponent via capture phase listeners.

              // Listen for functional mutations dispatched from outer Angular Shell layout
              window.addEventListener('message', function(event) {
                const msg = event.data;
                if (!msg || !msg.action) return;

                if (msg.action === 'REQUEST_SYNC') {
                  window.rapiderEditorApi.notifyMutation();
                }
              });
            },
            /**
             * @method createBadge
             * @description Disabled natively. Angular CDK handles the overlay bubbles exclusively now to support drag-and-drop.
             */
            createBadge: function(node, elementId) {
              // NO-OP
            },
            /**
             * @method refreshBadges
             * @description Cleans up any lingering badges from previous renders natively.
             */
            /**
             * @method refreshBadges
             * @description Cleans up any lingering badges from previous renders natively.
             */
            refreshBadges: function() {
              document.querySelectorAll('.rapider-comment-badge').forEach(b => b.remove());
            },
            notifyMutation: function() {
              const clonedDoc = document.documentElement.cloneNode(true);
              
              // CRITICAL GUARANTEE: Strip ALL badges and visual highlights from the cloned DOM before sending to Angular.
              clonedDoc.querySelectorAll('[data-rapider-injected="true"]').forEach(el => el.remove());
              clonedDoc.querySelectorAll('.rapider-comment-badge').forEach(b => b.remove());
              clonedDoc.querySelectorAll('.comment-hover-target').forEach(el => el.classList.remove('comment-hover-target'));
              clonedDoc.querySelectorAll('.rapider-selected-node').forEach(el => el.classList.remove('rapider-selected-node'));
              
              // CRITICAL FIX: Strip dynamically injected styles from CDN scripts (Tailwind & FontAwesome)
              clonedDoc.querySelectorAll('style').forEach(style => {
                if (style.textContent && style.textContent.includes('tailwindcss')) style.remove();
              });
              clonedDoc.querySelectorAll('style[id^="fa-"], link[id^="fa-"]').forEach(el => el.remove());
              
              // BULLETPROOF: Strip explicitly known injected CDN Scripts & Links based on URL
              clonedDoc.querySelectorAll('script').forEach(script => {
                const src = script.getAttribute('src') || '';
                if (src.includes('tailwindcss/browser') || src.includes('fontawesome.com') || src.includes('rapider-components.js')) {
                  script.remove();
                }
              });
              clonedDoc.querySelectorAll('link').forEach(link => {
                const href = link.getAttribute('href') || '';
                if (href.includes('ng-zorro-antd.min.css') || href.includes('rapider-tailwind-theme.css')) {
                  link.remove();
                }
              });
              clonedDoc.querySelectorAll('base').forEach(base => base.remove());

              /**
               * ARCHITECTURE UPDATE: 
               * We intentionally KEEP the semantic comment ID, text, and status in the HTML.
               * This serves as the absolute source of truth for mapping bubbles to elements on reload,
               * and feeds the AI explicit context natively in the DOM.
               * We only strip the coordinate/tracking attributes to keep the HTML clean.
               */
              clonedDoc.querySelectorAll('[data-rapider-comment-id]').forEach(el => {
                el.removeAttribute('data-rapider-comment-offset-x');
                el.removeAttribute('data-rapider-comment-offset-y');
                el.removeAttribute('data-rapider-comment-absolute-x');
                el.removeAttribute('data-rapider-comment-absolute-y');
                el.removeAttribute('data-rapider-comment-version-sha');
                el.removeAttribute('data-rapider-comment-context');
                el.removeAttribute('data-rapider-comment-index');
              });
              
              window.parent.postMessage({
                type: 'EDITOR_MUTATION',
                html: '<!DOCTYPE html>\\n' + clonedDoc.outerHTML
              }, '*');
            }
          };
          window.addEventListener('DOMContentLoaded', () => {
            window.rapiderEditorApi.init();
            window.rapiderEditorApi.refreshBadges();
          });
        </script>
        <style data-rapider-injected="true" id="rapider-builder-styles">
          .comment-hover-target { outline: 2px dashed #ef4444 !important; outline-offset: -2px; cursor: crosshair !important; }
          .show-comment-outlines [data-rapider-comment-id] { outline: 2px dashed #ff4500 !important; outline-offset: -2px; }
          .rapider-selected-node { outline: 3px solid #3b82f6 !important; outline-offset: -1px !important; }
        </style>
      `;
      completeHtml = completeHtml.replace(
        /<\/head>/i,
        (match) => `${editorSdkScript}\n${match}`,
      );
    }

    if (nonce > 0) {
      completeHtml = `${completeHtml}\n<!-- rapider-refresh:${nonce} -->`;
    }

    if (allowSameOrigin) {
      // Developer / DOM-inspection mode (Visual Builder, Bug Pinning, E2E Runner): use Blob URL with allow-same-origin
      this.iframeSrcdoc.set(null);
      const blob = new Blob([completeHtml], { type: 'text/html' });
      blobUrl = URL.createObjectURL(blob);
      this.iframeUrl.set(
        this.sanitizer.bypassSecurityTrustResourceUrl(blobUrl),
      );
    } else {
      // Isolated sandbox mode (default Installed App view): use [srcdoc] WITHOUT allow-same-origin
      // Eliminates Chrome's sandbox-escape warning and isolates parent DOM & localStorage JWT tokens.
      this.iframeUrl.set(null);
      this.iframeSrcdoc.set(this.sanitizer.bypassSecurityTrustHtml(completeHtml));
    }
  }

  /**
   * Clears SWR cache for the specified entities and reloads the active sandbox document.
   */
  public reloadSandbox(entitiesToInvalidate?: string[]): void {
    const entities = (entitiesToInvalidate || []).filter(Boolean);
    if (entities.length > 0) {
      this.postMessageToIframe({
        type: 'RAPIDER_CLEAR_SWR_CACHE',
        entities,
      });
    }
    this.refreshNonce.update((n) => n + 1);
  }

  /**
   * PURE ACTION: Posts a message securely to the sandboxed iframe.
   * Useful for sending fetched API data back into the raw HTML/JS context.
   * @param {any} message - The data payload to send to the iframe.
   */
  public postMessageToIframe(message: any): void {
    if (this.iframeElement && this.iframeElement.nativeElement.contentWindow) {
      this.iframeElement.nativeElement.contentWindow.postMessage(message, '*');
    }
  }

  /**
   * Called natively by the iframe (load) event to notify parent components
   * that the document is ready for interaction.
   */
  public iframeLoaded(): void {
    if (this.iframeElement) {
      const currentTheme = normalizeAppThemeKey(this.appTheme());
      if (currentTheme !== 'default') {
        void this.applyThemeToLiveIframe(currentTheme, this.assetBaseUrl());
      }
      this.onIframeLoad.emit(this.iframeElement.nativeElement);
    }
  }
}
